import { defineQuery } from "@rocicorp/zero";
import { z } from "zod";
import type {} from "../zero-context";
import { zql } from "../schema";
import { channelMemberGuard } from "./access-filters";

// Phase 6: agent-queryability shared queries for call lifecycle messages
// (kind ∈ call_started_video / call_started_audio / call_ended).
// Imperative one-shot reads. Channel-membership enforced via
// channelMemberGuard on the outer messages scope.
//
// NOTE (no current consumers): scaffolding for future agent paths.
// See calendar-events.ts header for the indexing concern that applies
// when these become hot in zero-cache (kind-filtered outer scope vs.
// partial indexes that don't replicate).

const CALL_KINDS = [
  "call_started_video",
  "call_started_audio",
  "call_ended",
] as const;

export const callEventsQueries = {
  // Call messages where `userId` participated. Joins via the
  // `call_participants` side table populated server-side by
  // actions.sendCallEvent.
  withUser: defineQuery(
    z.object({
      userId: z.string(),
      since: z.number().optional(),
      limit: z.number().default(100),
    }),
    ({ args: { userId, since, limit }, ctx: { sub } }) => {
      const base = zql.messages
        .where("kind", "IN", CALL_KINDS)
        .whereExists("call_participants", (cpq) => cpq.where("user_id", userId))
        .whereExists("channel", channelMemberGuard(sub));
      const filtered = since ? base.where("created_at", ">=", since) : base;
      return filtered
        .orderBy("created_at", "desc")
        .orderBy("id", "desc")
        .limit(limit)
        .related("call_participants");
    },
  ),
  // Call lifecycle messages in a channel. Default returns all 3 kinds
  // (call_started_video / call_started_audio / call_ended); pass
  // `kinds` to narrow to a specific subset (e.g. only "call_ended"
  // to filter completed calls).
  inChannel: defineQuery(
    z.object({
      channelId: z.string(),
      kinds: z.array(z.enum(CALL_KINDS)).optional(),
      limit: z.number().default(50),
    }),
    ({ args: { channelId, kinds, limit }, ctx: { sub } }) =>
      zql.messages
        .where("channel_id", channelId)
        .where("kind", "IN", kinds && kinds.length > 0 ? kinds : CALL_KINDS)
        .whereExists("channel", channelMemberGuard(sub))
        .orderBy("created_at", "desc")
        .orderBy("id", "desc")
        .limit(limit)
        .related("call_participants"),
  ),
};
